import { config } from "../../config";
import { ImagesService } from "../../server/cloudflare/images-service";
import { serviceURL } from "../../utils/service-url";
import { NameSpaceURL, uuidv4, uuidv5 } from "../../utils/uuid";

import type { StandardBrandKitStyle } from "../brand-kit/server/brand-kit-service";
import type { BrandInfoSelected } from "../brand-kit/types";
import type { ProjectData } from "./types";

export async function copyImage(
  getToken: () => Promise<string | null>,
  src: string,
  id: string,
): Promise<{ success: true; url: string } | { success: false }> {
  const token = await getToken();
  if (!token) throw new Error("No token");
  return await new ImagesService().upload(
    new File([await (await fetch(src)).blob()], id),
    uuidv5(NameSpaceURL, `dh:${id}`),
    token,
  );
}

export function imageURL(account_id: string, id: string, cacheBustNumber = 1): string {
  return `${serviceURL("images")}${account_id}/${uuidv5(
    NameSpaceURL,
    `dh:${id}`,
  )}?v=${cacheBustNumber}`;
}

const pages: Record<NonNullable<StandardBrandKitStyle>, number> = {
  bold: 1,
  sophisticated: 2,
  organic: 3,
  formal: 4,
  minimalist: 5,
};

export function getPageNumber(brandInfo?: BrandInfoSelected): number {
  if (brandInfo?.branding_type.type === "custom") return 0;

  return pages[brandInfo?.branding_type.value ?? "bold"];
}

async function fetchProjectPages(
  accessToken: string,
  format: string,
  projectId: string,
): Promise<{ pages: { page_id: string; page_number: number }[]; projectId: string }> {
  const projectResp = await fetch(
    `https://${config.design_huddle.domain}/api/projects/${projectId}`,
    {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { authorization: `Bearer ${accessToken}` },
    },
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const { success: projectSuccess, data: projectData } = (await projectResp.json()) as {
    success: boolean;
    data: {
      pages: { page_id: string; page_number: number }[];
      scenes: { scene_id: string; scene_number: number }[];
    };
  };

  if (!projectSuccess || !projectData) throw new Error("Project Error");

  return format === "mp4"
    ? {
        pages: projectData.scenes?.map((scene) => ({
          page_id: scene.scene_id,
          page_number: scene.scene_number,
        })),
        projectId,
      }
    : { pages: projectData.pages, projectId };
}

export async function getDesignHuddleRenderUrls({
  getToken,
  accessToken,
  projectId,
  format = "png",
  filename = "file",
  dimensions,
  onProgress,
}: {
  getToken: () => Promise<string>;
  accessToken: string;
  projectId: string;
  format?: string;
  filename?: string;
  dimensions?: { width: number; height: number };
  onProgress?: (progress: number | null) => void;
}): Promise<string[]> {
  const projectPages = await fetchProjectPages(accessToken, format, projectId);

  if (format === "png") {
    return await Promise.all(
      projectPages.pages.map((page) =>
        getDesignHuddleProjectRender({
          getToken,
          accessToken,
          projectPages,
          newImageId: `${projectId}-${page.page_number}`,
          pageNumber: page.page_number,
          format,
          filename,
          dimensions,
          onProgress,
        }),
      ),
    );
  }

  return [
    await getDesignHuddleProjectRender({
      getToken,
      accessToken,
      projectPages,
      newImageId: projectId,
      format,
      filename,
      dimensions,
      onProgress,
    }),
  ];
}

export async function getDesignHuddleRender({
  getToken,
  accessToken,
  projectId,
  pageNumber,
  format = "png",
  filename = "file",
  dimensions,
  onProgress,
}: {
  getToken: () => Promise<string>;
  accessToken: string;
  projectId: string;
  pageNumber?: number;
  format?: string;
  filename?: string;
  dimensions?: { width: number; height: number };
  onProgress?: (progress: number | null) => void;
}): Promise<string> {
  const projectPages = await fetchProjectPages(accessToken, format, projectId);

  return getDesignHuddleProjectRender({
    getToken,
    accessToken,
    projectPages,
    newImageId: projectId,
    pageNumber,
    format,
    filename,
    dimensions,
    onProgress,
  });
}

export async function getDesignHuddleProjectRender({
  getToken,
  accessToken,
  projectPages,
  newImageId,
  customizations,
  dimensions,
  pageNumber,
  format = "png",
  filename = "file",
  onProgress,
}: {
  getToken: () => Promise<string | null>;
  accessToken: string;
  projectPages: { pages: { page_id: string; page_number: number }[]; projectId: string };
  newImageId: string;
  customizations?: { [key: string]: unknown };
  dimensions?: { width: number; height: number };
  pageNumber?: number;
  format?: string;
  filename?: string;
  onProgress?: (progress: number | null) => void;
}): Promise<string> {
  const page_id = projectPages.pages?.find((p) => p.page_number === pageNumber)?.page_id;

  const body = {
    format,
    page_id,
    filename,
    customizations,
    ...dimensions,
  };

  return await exportRender({
    body,
    getToken,
    accessToken,
    projectId: projectPages.projectId,
    format,
    newImageId,
    onProgress,
  });
}

export async function getDesignHuddleProjectRenderFromPageId({
  getToken,
  accessToken,
  projectId,
  pageId,
  format,
  filename,
  onProgress,
}: {
  getToken: () => Promise<string | null>;
  accessToken: string;
  projectId: string;
  pageId?: string;
  format: string;
  filename: string;
  onProgress?: (progress: number | null) => void;
}): Promise<string> {
  const body = {
    format,
    filename,
    ...(pageId ? { page_id: pageId } : {}),
  };

  return await exportRender({
    body,
    getToken,
    accessToken,
    projectId,
    format,
    newImageId: uuidv4(),
    onProgress,
  });
}

async function exportRender({
  body,
  getToken,
  accessToken,
  projectId,
  format,
  newImageId,
  onProgress,
}: {
  body: unknown;
  getToken: () => Promise<string | null>;
  accessToken: string;
  projectId: string;
  format: string;
  newImageId: string;
  onProgress?: (progress: number | null) => void;
}): Promise<string> {
  const resp = await fetch(
    `https://${config.design_huddle.domain}/api/projects/${projectId}/export`,
    {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: { authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    },
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const { success, data } = (await resp.json()) as {
    success: boolean;
    data: { job_id: string };
  };
  if (!success || !data || !data.job_id) throw new Error("Export Error");

  const download_url = await waitForUrl(accessToken, projectId, data.job_id, 1, onProgress);

  if (format === "pdf") return download_url;

  onProgress?.(99);
  const image = await copyImage(getToken, download_url, newImageId);
  if (!image.success) throw new Error("Export Error");
  onProgress?.(100);

  const cacheBust = `?v=${Date.now()}`;
  return `${image.url}${cacheBust}`;
}

async function waitForUrl(
  accessToken: string,
  project_id: string,
  job_id: string,
  attempt: number,
  onProgress?: (progress: number | null) => void,
): Promise<string> {
  const resp = await fetch(
    `https://${config.design_huddle.domain}/api/projects/${project_id}/export/jobs/${job_id}`,
    {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { authorization: `Bearer ${accessToken}` },
    },
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const { success, data } = (await resp.json()) as
    | {
        success: boolean;
        data: { completed: true; download_url: string };
      }
    | {
        success: boolean;
        data: { completed: false; progress_percentage: number };
      };

  if (!success || !data || (!data.completed && attempt < 10)) {
    if (!data.completed && onProgress) onProgress(data.progress_percentage);

    await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
    return waitForUrl(accessToken, project_id, job_id, attempt + 1, onProgress);
  } else if (data.completed) {
    return data.download_url;
  }

  throw new Error("Export Job Timeout");
}

export function getProgressText(progress: number | null): string {
  if (progress === null) return "";

  if (progress <= 25) {
    return "Updating...";
  } else if (progress <= 50) {
    return "Enhancing...";
  } else if (progress <= 75) {
    return "Optimizing...";
  } else {
    return "Finalizing...";
  }
}

export async function getProjectData(projectId: string, token: string): Promise<ProjectData> {
  if (!token) throw new Error("No token");

  const resp = await fetch(
    `https://${config.design_huddle.domain}/api/projects/${projectId}/data`,
    {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { authorization: `Bearer ${token}` },
    },
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const { success, data } = (await resp.json()) as {
    success: boolean;
    data: ProjectData;
  };

  if (!success || !data) throw new Error("Failed to fetch project data");
  return data;
}
